import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Upload, X, Paperclip, Loader2 } from "lucide-react";

/**
 * FileUpload — uploads file(s) via /api/files/upload, returns array of file metadata.
 * Props:
 *  - value: array of { id, filename } currently attached
 *  - onChange: (newArray) => void
 *  - accept: string (default images + pdfs)
 *  - testId: prefix for data-testid
 */
export default function FileUpload({ value = [], onChange, accept = "image/*,application/pdf", testId = "file" }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const upload = async (files) => {
    if (!files || !files.length) return;
    setErr(""); setBusy(true);
    try {
      const added = [];
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        added.push({ id: data.id, filename: data.filename, content_type: data.content_type });
      }
      onChange?.([...(value || []), ...added]);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const remove = (idx) => onChange?.(value.filter((_, i) => i !== idx));

  return (
    <div>
      <label className="inline-flex items-center gap-2 text-xs font-semibold bg-white border border-zinc-200 hover:border-[#F97316] hover:text-[#F97316] py-2 px-3 rounded-md cursor-pointer" data-testid={`${testId}-upload-label`}>
        {busy ? <Loader2 className="animate-spin" size={13} /> : <Upload size={13} />}
        {busy ? "Uploading…" : "Attach photo / PDF"}
        <input type="file" accept={accept} multiple className="hidden" data-testid={`${testId}-upload-input`}
               onChange={(e)=>upload(Array.from(e.target.files || []))} />
      </label>
      {err && <div className="text-xs text-rose-600 mt-1">{err}</div>}
      {value?.length > 0 && (
        <div className="mt-2 space-y-1">
          {value.map((f, i) => (
            <div key={i} className="inline-flex items-center gap-2 text-xs bg-zinc-100 rounded-full pl-3 pr-1 py-1 mr-1.5">
              <Paperclip size={11} className="text-zinc-500" />
              <span className="font-medium">{f.filename}</span>
              <button type="button" onClick={()=>remove(i)} className="text-zinc-500 hover:text-rose-600 p-0.5 rounded-full" data-testid={`${testId}-remove-${i}`}>
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
